import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Card, CardContent, Typography, Table, TableBody,
    TableCell, TableHead, TableRow, Button, Chip, Alert, IconButton, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControlLabel, Switch
} from '@mui/material';
import { AccountBalance, Home, Add, Edit, Delete, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { useGLAccounts } from '../hooks/useGLAccounts';
import { glAccountOptions } from '../utils/glAccountOptions';
import { fmt, sym } from '../utils/currency';

const emptyForm = {
    accountNo: '',
    bankName: '',
    accountName: '',
    glAccount: '',
    isActive: true
};

const BankList = () => {
    const navigate = useNavigate();
    const [banks, setBanks] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingAccountNo, setEditingAccountNo] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const { attemptClose, discardDialog } = useUnsavedGuard({ open: dialogOpen, value: form, onClose: () => setDialogOpen(false) });
    const { accounts: glAccounts } = useGLAccounts();

    // Deactivate confirm dialog
    const [deactivateDlg, setDeactivateDlg] = useState(null);

    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

    const fetchBanks = async () => {
        try {
            const { data } = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/finance/banks`,
                { headers }
            );

            setBanks(data); // Backend now returns banks with balances
            setError('');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load banks');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBanks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCreate = () => {
        setEditingAccountNo(null);
        setForm(emptyForm);
        setDialogOpen(true);
    };

    const handleEdit = (b) => {
        setEditingAccountNo(b.accountNo);
        setForm({
            accountNo: b.accountNo,
            bankName: b.bankName,
            accountName: b.accountName,
            glAccount: b.glAccount,
            isActive: b.isActive !== undefined ? b.isActive : true
        });
        setDialogOpen(true);
    };

    const confirmAction = async () => {
        try {
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/api/finance/banks/${deactivateDlg}`,
                { headers }
            );
            setDeactivateDlg(null);
            setError('Bank account deactivated successfully');
            fetchBanks();
        } catch (err) {
            setDeactivateDlg(null);
            setError(err.response?.data?.error || 'Failed to deactivate bank account');
        }
    };

    const handleSave = async () => {
        if (!editingAccountNo && !form.accountNo.trim()) {
            setError('Account No is required');
            return;
        }
        if (!form.bankName.trim() || !form.accountName.trim() || !form.glAccount) {
            setError('Bank Name, Account Name and GL Account are required');
            return;
        }

        try {
            if (editingAccountNo) {
                await axios.put(
                    `${process.env.REACT_APP_API_URL}/api/finance/banks/${editingAccountNo}`,
                    {
                        bankName: form.bankName,
                        accountName: form.accountName,
                        glAccount: form.glAccount,
                        isActive: form.isActive
                    },
                    { headers }
                );
            } else {
                await axios.post(
                    `${process.env.REACT_APP_API_URL}/api/finance/banks`,
                    {
                        accountNo: form.accountNo,
                        bankName: form.bankName,
                        accountName: form.accountName,
                        glAccount: form.glAccount
                    },
                    { headers }
                );
            }
            setDialogOpen(false);
            setError('');
            fetchBanks();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save bank account');
        }
    };

    if (loading) {
        return (
            <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4, textAlign: 'center' }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Loading bank accounts...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4 }}>
            <Card elevation={3}>
                <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                        <Box display="flex" alignItems="center">
                            <AccountBalance
                                sx={{ mr: 1, color: 'primary.main' }}
                                onClick={() => navigate('/dashboard')}
                                title="Back to Dashboard"
                                tooltip="Back to Dashboard"
                                />
                            <Typography variant="h5" color="primary">
                                Bank Accounts
                            </Typography>
                        </Box>

                        <Box>
                            <IconButton
                                color="primary"
                                onClick={() => navigate('/dashboard')}
                                title="Back to Dashboard"
                                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' }, mr: 1 }}
                            >
                                <Home />
                            </IconButton>
                            <IconButton
                                color="primary"
                                onClick={handleCreate}
                                title="Create New Bank Account"
                                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}
                            >
                                <Add />
                            </IconButton>
                        </Box>
                    </Box>

                    {error && <Alert severity={error.includes('success') ? 'success' : 'error'} sx={{ mb: 2 }}>{error}</Alert>}

                    {banks.length === 0 ? (
                        <Typography>No bank accounts found.</Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Account No</strong></TableCell>
                                    <TableCell><strong>Bank</strong></TableCell>
                                    <TableCell><strong>Name</strong></TableCell>
                                    <TableCell><strong>GL Account</strong></TableCell>
                                    <TableCell align="right"><strong>Balance</strong></TableCell>
                                    <TableCell><strong>Status</strong></TableCell>
                                    <TableCell><strong>Actions</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {banks.map(b => (
                                    <TableRow
                                        key={b.accountNo}
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => navigate(`/banks/${b.accountNo}`)}
                                    >
                                        <TableCell>{b.accountNo}</TableCell>
                                        <TableCell>{b.bankName}</TableCell>
                                        <TableCell>{b.accountName}</TableCell>
                                        <TableCell>{b.glAccount}</TableCell>
                                        <TableCell align="right">
                                            <Chip
                                                label={`${sym()} ${fmt(b.balance ?? 0)}`}
                                                color={(b.balance ?? 0) >= 0 ? 'success' : 'error'}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={b.isActive === false ? 'Inactive' : 'Active'}
                                                color={b.isActive === false ? 'default' : 'success'}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                color="primary"
                                                onClick={(e) => { e.stopPropagation(); handleEdit(b); }}
                                                title="Edit Bank Account"
                                                sx={{ mr: 1 }}
                                            >
                                                <Edit />
                                            </IconButton>
                                            <IconButton
                                                color="warning"
                                                onClick={(e) => { e.stopPropagation(); setDeactivateDlg(b.accountNo); }}
                                                title="Deactivate Bank Account"
                                                disabled={b.isActive === false}
                                                sx={{ mr: 1 }}
                                            >
                                                <Delete />
                                            </IconButton>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/banks/${b.accountNo}`);
                                                }}
                                            >
                                                View Ledger
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Deactivate Confirm Dialog */}
            <Dialog open={Boolean(deactivateDlg)} onClose={() => setDeactivateDlg(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}>
                    <WarningAmber color="warning" /> Deactivate Bank Account
                </DialogTitle>
                <DialogContent>
                    <Typography>Deactivate account <strong>{deactivateDlg}</strong>?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeactivateDlg(null)}>Cancel</Button>
                    <Button variant="contained" color="warning" onClick={confirmAction}>Deactivate</Button>
                </DialogActions>
            </Dialog>

            {discardDialog}
            <Dialog open={dialogOpen} onClose={attemptClose} fullWidth maxWidth="sm">
                <DialogTitle>{editingAccountNo ? `Edit Bank Account: ${editingAccountNo}` : 'New Bank Account'}</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        <TextField
                            label="Account No"
                            value={form.accountNo}
                            onChange={(e) => setForm({ ...form, accountNo: e.target.value })}
                            disabled={!!editingAccountNo}
                            fullWidth
                        />
                        <TextField
                            label="Bank Name"
                            value={form.bankName}
                            onChange={(e) => setForm({ ...form, bankName: e.target.value })}
                            fullWidth
                        />
                        <TextField
                            label="Account Name"
                            value={form.accountName}
                            onChange={(e) => setForm({ ...form, accountName: e.target.value })}
                            fullWidth
                        />
                        <TextField
                            select
                            label="GL Account"
                            value={form.glAccount}
                            onChange={(e) => setForm({ ...form, glAccount: e.target.value })}
                            fullWidth
                        >
                            {glAccountOptions(glAccounts, { noneLabel: 'Select GL Account' })}
                        </TextField>
                        {editingAccountNo && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={form.isActive}
                                        onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                                    />
                                }
                                label="Active"
                            />
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={attemptClose}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BankList;
